import "xstyle/css!app/css/ol3-layerswitcher.css";
import "xstyle/css!ol-css/ol.css";

import $ = require("jquery");
import _ = require("underscore");
import ol = require("ol");
import proj4 = require("proj4");
import template = require("../templates/layout-map-template");
import LayerFactory = require("../layer-factory/native");
import Utils = require("../utils");
import geo = require("../utils/geo");
import Portal = require("portal-gis");
import Base = require("./base-view");
import Model = require("../models/base-model");
import ResizeSensor = require("../utils/resize-sensor");
import globals = require("../../globals");
const log = Utils.log;

const events = {
  map: "moveend,postrender,postcompose,precompose,render,click,rightclick,dblclick,mousedown,mouseup,mouseover,mouseout,mousemove,mouseenter,mouseleave,selectstart,wheel,keypress,keydown,keyup,blur,focus,deactivate,DOMFocusIn,DOMFocusOut,change,select,submit,input,propertychange,dragstart,drag,dragenter,dragover,dragleave,drop,dragend,touchstart,touchmove,touchend,touchcancel,beforeunload,consolemessage,contextmenu,DOMContentLoaded,error,help,load,losecapture,orientationchange,readystatechange,resize,scroll,unload,hashchange,pagehide,pageshow,popstate,copy,paste,cut,beforecopy,beforecut,beforepaste,online,offline,message,connect,webkitAnimationStart,webkitAnimationEnd,webkitAnimationIteration,webkitTransitionEnd,pointerdown,pointerup,pointercancel,pointermove,pointerover,pointerout,pointerenter,pointerleave,gotpointercapture,lostpointercapture,MSGestureChange,MSGestureEnd,MSGestureHold,MSGestureStart,MSGestureTap,MSGotPointerCapture,MSInertiaStart,MSLostPointerCapture,MSPointerCancel,MSPointerDown,MSPointerEnter,MSPointerHover,MSPointerLeave,MSPointerMove,MSPointerOut,MSPointerOver,MSPointerUp,text,textInput,compositionstart,compositionupdate,compositionend,exit,loadabort,loadcommit,loadredirect,loadstart,loadstop,responsive,sizechanged,unresponsive,visibilitychange,storage,DOMSubtreeModified,DOMNodeInserted,DOMNodeRemoved,DOMNodeRemovedFromDocument,DOMNodeInsertedIntoDocument,DOMAttrModified,DOMCharacterDataModified"
    .split(",")
    .sort(),
  view: "center,resolution,rotation".split(",").sort(),
};
const ADDRESS="Hansen.Property.Address";
const PARCEL="Hansen.Property.Parcel";
const ASSET="Hansen.AssetManagement.Asset";
const PROPERTY="";

class MapView extends Base<Portal.MapDefinitionElement> {
  static about = `
Manages all things map including creating layers.

Triggers a corresponding event when any of the following map events occur: 

${events.map.join("\t\n")}

The difference being that the event name is prefixed with "map-".

Similarly, triggers a "map-" event when any of the following view "change" events occur: 

${events.view.join("\t\n")}

Identify any of these events using an event handlers _event_ attribute to respond to map/view changes.

Sample Usage:

    <EVENT 
        about="extension placeholder, logs the maps viewstate" 
        event="map-resolution" 
        id="map-event-handler" 
        mid="app/commands/map-event-handler" />

In this example the map-event-handler fires each time the map resolution changes.

`;

  static options = {
    className: {
      about: "css className",
      default: "map",
    },
    "init-center": {
      about: "Set the coordinates on which the map will be centered when it is first loaded.",
    },
    "init-zoom": {
      about: "Set the initial zoom level of the map.",
    },
    "min-zoom": {
      about: "Specify the maximum zoom level at which the map will be visible.",
    },
    "max-zoom": {
      about: "Specify the minimum zoom level at which the map will be visible.",
    },
    extent: {
      about: "Set the geographical extent of the map.",
    },
    maplet: {
      about: "maplet configuration",
    },
    model: {
      about: "model of maplet configuration",
    },
    template: {
      about: "map layout template",
      default: "app/templates/layout-map-template",
    },
    wkid: {
      about: "Spatial Referencing System Well-known ID",
      default: "EPSG:3857",
    },
  };

  private map: ol.Map;

  // TEMPORARY: replace internal type to transition this into a base-view managed by a controller
  options: {
    template: typeof template;
    wkid: string;
    maplet: Portal.MapApplicationElement;
  };

  // extent can be set before map is ready so store it here
  private _extent: Rhythm.GIS.Spatial<Rhythm.GIS.Extent>;

  execute(options = this.options) {
    _.defaults(options, {
      template: template,
      wkid: "EPSG:3857", // required by most public basemaps
      className: "map",
      model: new Model<Portal.MapDefinitionElement>(options.maplet),
    });
    return super.execute(options);
  }

  private project(srs: Rhythm.GIS.SpatialReference) {
    var mapView = this.map.getView();
    var mapSrs = mapView.getProjection().getCode();
    Utils.ifOption(<any>this.options, "wkt", (wkt) => (mapSrs = <string>wkt));
    var proj = proj4(srs.wkid || srs.wkt, mapSrs);
    return proj;
  }

  private transform(proj, points: number[], inverse = false) {
    var result = <Array<number>>[];

    let f = proj[inverse ? "inverse" : "forward"];
    for (var i = 0; i < points.length; i += 2) {
      var p = f([points[0 + i], points[1 + i]]);
      result.push(p[0], p[1]);
    }

    return result;
  }

  getProjection() {
    return this.map.getView().getProjection();
  }

  addControl(control: ol.control.Control) {
    this.map.addControl(control);
  }

  get extent() {
    if (this.map) {
      let proj = this.project({ wkid: "EPSG:4326" });
      let extent = this.map.getView().calculateExtent(this.map.getSize());
      this._extent = this._extent || {
        geometry: null,
        spatialReference: null,
      };
      this._extent.geometry = this.transform(proj, extent, true);
    }
    return this._extent;
  }

  set extent(extent: Rhythm.GIS.Spatial<Rhythm.GIS.Extent>) {
    this._extent = extent;

    let doit = () => {
      extent = this._extent;
      if (!extent) return;
      var mapView = this.map.getView();
      var proj = this.project(extent.spatialReference || { wkid: "EPSG:4326" });
      var olExtent = this.transform(proj, extent.geometry);
      geo.fixExtent(olExtent);
      //YAH-001: only set the extent if it is valid
      if (olExtent.every((v) => !isNaN(v))) {
        mapView.fit(olExtent, this.map.getSize());
      } else {
        console.warn("invalid extent:", olExtent);
      }
    };

    this.map ? doit() : Utils.once(this.model, "map", doit);
  }

  createLayer(layer: Portal.MapLayerLeaf) {
    log("creating layer: ", layer.id);

    var d = $.Deferred<ol.layer.Base>();

    var Factory = LayerFactory;

    // TODO: use Utils.withModule?
    requirejs([layer.type], (LayerFactory) => {
      Factory = LayerFactory;
      let req = $.when(
        Factory.create({
          layer: layer,
          view: this,
          mediator: this.mediator,
        })
      );

      req.done((nativeLayer) => {
        nativeLayer.set("id", layer.id);
        d.resolve(nativeLayer);
      });

      req.fail((err) => {
        console.error("unable to create layer:", layer);
        d.reject(err);
      });
    });

    return d;
  }

  addLayer(layer: Portal.MapLayerLeaf) {
    log("adding layer: ", layer.id);
    var d = $.Deferred<ol.layer.Base>();
    this.createLayer(layer).done((nativeLayer) => {
      Utils.once(this.model, "map", () => {
        if (layer.basemap) {
          this.map.getLayers().insertAt(0, nativeLayer);
        } else {
          this.map.addLayer(nativeLayer);
        }
        d.resolve(nativeLayer);
      });
    });
    return d;
  }

  getLayers(group: ol.layer.Group | boolean = true) {
    if (typeof group === "boolean") {
      let layers = this.map.getLayers().getArray();
      let result = layers.filter((l) => !(l instanceof ol.layer.Group));
      if (group) {
        let groupLayers = layers.filter((l) => l instanceof ol.layer.Group).map((l) => <ol.layer.Group>l);
        groupLayers.forEach((g) => this.getLayers(g).forEach((l) => result.push(l)));
      }
      return result;
    } else {
      let layers = group.getLayers().getArray();
      let result = layers.filter((l) => !(l instanceof ol.layer.Group));
      let groupLayers = layers.filter((l) => l instanceof ol.layer.Group).map((l) => <ol.layer.Group>l);
      groupLayers.forEach((g) => this.getLayers(g).forEach((l) => result.push(l)));
      return result;
    }
  }

  private getSublayer(id: string, layers?: Array<ol.layer.Base>) {
    // this id might be within a group layer
    
    var sublayer: ol.layer.Base;
    layers = layers || this.map.getLayers().getArray();
    if (!layers.length) return sublayer;

    var groupLayers = <Array<ol.layer.Group>>layers.filter((l) => l instanceof ol.layer.Group);

    groupLayers.some((groupLayer) =>
      groupLayer
        .getLayers()
        .getArray()
        .filter((l) => l.get("id") === id)
        .some((layer) => {
          sublayer = layer;
          return true;
        })
    );

    if (!!sublayer) return sublayer;

    layers = <any>_.flatten(groupLayers.map((groupLayer) => groupLayer.getLayers().getArray()));

    return this.getSublayer(id, layers);
  }

  private getLayer(id: string, searchGroups = false) {
    var layers = this.map.getLayers().getArray();
    var result = layers.filter((l) => l.get("id") === id)[0];
    if (!result && searchGroups) result = this.getSublayer(id);
    return result;
  }

  private setLayer(id: string, args: any, searchGroups = false) {
    var nativeLayer = this.getLayer(id, searchGroups);
    if (!nativeLayer) return false;
    for (var key in args) {
      nativeLayer.set(key, args[key]);
    }
    return true;
  }

  removeLayer(id: string) {
    var nativeLayer = this.getLayer(id);
    nativeLayer && this.map.removeLayer(nativeLayer);
  }

  showLayer(id: string) {
    var layer = this.getLayer(id, true);
    if (layer && !layer.get("visible")) {
      layer.set("visible", true);
      this.trigger("show-layer", { id: id, layer: layer });
    }
  }

  hideLayer(id: string) {
    var layer = this.getLayer(id, true);
    if (layer && layer.get("visible")) {
      layer.set("visible", false);
      this.trigger("hide-layer", { id: id, layer: layer });
    }
  }

  isLayerVisible(id: string) {
    var l = this.getLayer(id, true);
    if (!l) {
      console.warn("layer not found");
      return false;
    }
    return !!l.get("visible");
  }

  // TODO: not correct! need a layers-handler command to watch the api.layers collection
  onRender() {
    var	selectedLayerList = [];

    selectedLayerList = Utils.deep(globals.layerList);
    var layers = this.model.data.Layers.Layers || [];

    // put baseLayers in the back
    var baseLayers = layers.filter((l) => l.basemap && !l.disabled);
    var overlayLayers = layers.filter((l) => !l.basemap && !l.disabled);


    if(selectedLayerList.length > 0){
      var selectedCompLayers = overlayLayers.filter((l)=> l.componentType != undefined && l.componentType == this.isSelectedCompLayer(l.componentType, selectedLayerList) || l.componentType == null || l.componentType == '');
      var selectedAssetLayers = overlayLayers.filter((l)=> l.assetType != undefined && l.assetType == this.isSelectedAssetLayer(l.assetType, selectedLayerList));
      layers = [].concat(baseLayers, selectedCompLayers, selectedAssetLayers);            
    } else{
      layers = [].concat(baseLayers, overlayLayers);
    }
    
    // load factories
    var mids = layers.filter((l) => !!l.type).map((l) => l.type);
    mids = _.uniq(mids).sort();

    log("loading layer factories: ", _.unique(mids).sort());
    Utils.loadModules(_.unique(mids).map((id) => ({ mid: id }))).done((mods) => {
      log("layer factories loaded, creating layers");

      {
        let options = Utils.asDictionary(this.options.maplet.Options.Values);

        let viewOptions = <olx.ViewOptions>{
          constrainRotation: true,
          enableRotation: options["enable-rotation"] === "true",
          zoom: 10,
          minZoom: 1,
          maxZoom: 28,
        };

        {
          let viewport = this.model.data.Viewport;
          if (viewport.extent) {
            viewOptions.extent = this.transform(
              proj4("EPSG:4326", this.options.wkid),
              Utils.split(viewport.extent).map(parseFloat)
            );
          }

          if (viewport.LODS && viewport.LODS.LODS.length) {
            let lods = viewport.LODS.LODS;
            viewOptions.minZoom = lods[0].level;
            viewOptions.maxZoom = lods[lods.length - 1].level;
          }
        }

        Utils.ifOption(options, "init-center", (v) => {
          log(`center: ${v}`);
          viewOptions.center = this.transform(proj4("EPSG:4326", this.options.wkid), Utils.split(v).map(parseFloat));
        });

        Utils.ifOption(options, "init-zoom", (v) => {
          log(`zoom: ${v}`);
          viewOptions.zoom = parseInt(v, 10);
        });

        Utils.ifOption(options, "min-zoom", (v) => {
          log(`min-zoom: ${v}`);
          viewOptions.minZoom = parseInt(v, 10);
        });

        Utils.ifOption(options, "max-zoom", (v) => {
          log(`max-zoom: ${v}`);
          viewOptions.maxZoom = parseInt(v, 10);
        });

        Utils.ifOption(options, "extent", (v) => {
          log(`constraining extent: ${v}`);
          viewOptions.extent = this.transform(proj4("EPSG:4326", this.options.wkid), Utils.split(v).map(parseFloat));
        });

        let mapView = new ol.View(viewOptions);

        let interactionOptions: olx.interaction.DefaultsOptions = {
          altShiftDragRotate: false,
          doubleClickZoom: false,
          keyboard: false,
          mouseWheelZoom: false,
          shiftDragZoom: false,
          dragPan: false,
          pinchRotate: false,
          pinchZoom: false,
          zoomDelta: 0,
          zoomDuration: 500,
        };

        Utils.ifOption(options, "default-interactions", (v) => {
          let options = JSON.parse(v);
          interactionOptions = _.defaults(options, interactionOptions);
        });

        let controlOptions: olx.control.DefaultsOptions = {
          attribution: false,
          rotate: false,
          zoom: false,
          zoomOptions: {
            zoomInTipLabel: "+",
            zoomOutLabel: "-",
            duration: 500,
          },
        };

        Utils.ifOption(options, "default-controls", (v) => {
          let options = JSON.parse(Utils.translate(v));
          controlOptions = _.defaults(options, controlOptions);
        });

        const mapOptions = <olx.MapOptions>{
          interactions: ol.interaction.defaults(interactionOptions),
          controls: ol.control.defaults(controlOptions),
          target: this.el,
          view: mapView,
        };

        var map = (this.map = new ol.Map(mapOptions));

        Utils.ifOption(options, "resize-sensor", (v) => {
          if (v !== "false") {
            new ResizeSensor(this.el, () => {
              map.updateSize();
              this.trigger("resize", {
                size: map.getSize(),
              });
            });
          }
        });

        this.model.set("map", map);

        events.map.map((event) =>
          map.on(event, (args) => {
            this.trigger("map-" + event, args);
          })
        );

        events.view.map((event) =>
          map.getView().on("change:" + event, (args) => {
            this.trigger("map-" + event, args);
          })
        );

        this.trigger("map-resolution", {
          hack: "allow ags-layers setMinMaxZoomLevel helper to compute the min/max resolutions based on the current map resolution and zoom",
        });

        // can be moved to configuration. the ribble style is in civics css for now
        let view = <Marionette.View<any>>this.controller.view;
        view.$el.find(".ol-viewport").bind("click", (e) => {
          if (view.$el.data("dragging")) return;
          view.$el.find(".civics-map-ripple").remove();
          // Setup
          var buttonWidth = 30,
            buttonHeight = 30;
          // Add the element
          view.$el.prepend("<span class='civics-map-ripple'></span>");
          // Make it round!
          if (buttonWidth >= buttonHeight) {
            buttonHeight = buttonWidth;
          } else {
            buttonWidth = buttonHeight;
          }
          // Get the center of the element
          var x = e.offsetX - buttonWidth / 2;
          var y = e.offsetY - buttonHeight / 2;
          // Add the ripples CSS and start the animation
          view.$el
            .find(".civics-map-ripple")
            .css({
              width: buttonWidth,
              height: buttonHeight,
              top: y + "px",
              left: x + "px",
            })
            .addClass("civics-map-ripple-effect");
        });
        map.on("moveend", (evt) => {
          setTimeout(() => view.$el.data("dragging", false), 1);
        });
        map.on("pointerdrag", (evt) => {
          view.$el.data("dragging", true);
        });

        let factory = <Rhythm.GIS.Dictionary<typeof LayerFactory>>mods.factory;
        // TODO: why not call createLayer instead?
        let eachLayerReq = Utils.when(
          layers
            .filter((l) => !!factory[l.type])
            .sort((a, b) => <any>b.basemap || 0 - <any>a.basemap || 0)
            .map((l) =>
              factory[l.type].create({
                layer: l,
                view: this,
                mediator: this.mediator,
              })
            )
        );

        eachLayerReq.done((nativeLayers) => {
          nativeLayers.forEach((l) => map.addLayer(l));

          log(
            "layers created: ",
            nativeLayers.map((l) => l.get("id"))
          );

          let codes = nativeLayers
            .filter((l) => !!l["getSource"])
            .map((l) => <ol.source.Source>l["getSource"]())
            .filter((l) => !!l)
            .map((l) => l.getProjection())
            .filter((l) => !!l)
            .map((l) => l.getCode());

          codes = _.unique(codes).filter((c) => c !== this.options.wkid);

          codes.forEach((code) => console.warn("srs mismatch: ", code));
        });

        eachLayerReq.fail((err) => {
          console.error("unable to consolidate layers:", err);
        });
      }
    });
  }
  isSelectedCompLayer(s1:string, cva: Array<any>){
    let finalString="";
    if(s1 != undefined){
    cva.forEach(function(value,elem){
       var compType = cva[elem].ipsComponentType;
       if(compType.toLowerCase() != ASSET.toLowerCase() && s1.toLowerCase() == compType.toLowerCase()){         
           finalString = compType;
           return;
       };

   });
  }
    return finalString;
}
isSelectedAssetLayer(s2:string, cva: Array<any>){
  let finalString="";
  if(s2 != undefined){
  cva.forEach(function(value,elem){
    var compType = cva[elem].ipsComponentType;
     var assetType = cva[elem].assetType;
     if(assetType != undefined){
      if(compType.toLowerCase() == ASSET.toLowerCase() && s2.toString() == assetType.toString()){         
          finalString = assetType;
          return;
      };
    }
 });
}
  return finalString;
}
  onBeforeDestroy() {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
  }

  private pan(direction: number, scale = 1) {
    let view = this.map.getView();
    let center = view.getCenter().map((v) => v); // need to clone otherwise change is not detected
    let extent = view.calculateExtent(this.map.getSize());
    let d = (extent[direction % 4] - center[direction % 2]) * scale;
    center[direction % 2] += d;
    view.setCenter(center);
  }

  zoom(zoom: number) {
    var view = this.map.getView();
    var zoom = view.getZoom() + zoom;
    view.setZoom(zoom);
  }

  zoomIn() {
    this.zoom(1);
  }

  zoomOut() {
    this.zoom(-1);
  }

  panLeft() {
    this.pan(0);
  }

  panDown() {
    this.pan(1);
  }

  panRight() {
    this.pan(2);
  }

  panUp() {
    this.pan(3);
  }

  panTo(center: ol.Coordinate) {
    var view = this.map.getView();
    let proj = this.project({ wkid: "EPSG:4326" });
    center = this.transform(proj, center);
    view.setCenter(center);
  }

  getState() {
    let view = this.map.getView();
    return <
      {
        resolution: number;
      }
    >view.getState();
  }
}

export = MapView;
